import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Compass, LayoutDashboard, MapPinned, Sparkles, Building2, Users, LogOut, Hotel as HotelIcon, Brain, Wand2, Anchor, DatabaseBackup, Megaphone, BarChart3, Handshake, MessagesSquare } from "lucide-react";
import { useAuth } from "@/lib/auth";

const navItems = [
  { to: "/dashboard", label: "Itinerarios", icon: LayoutDashboard, tid: "nav-itineraries" },
  { to: "/curated-trips", label: "Curated Trips", icon: Anchor, tid: "nav-curated", end: true },
  { to: "/experiences", label: "Experiencias", icon: Sparkles, tid: "nav-experiences" },
  { to: "/hotels", label: "Hoteles", icon: HotelIcon, tid: "nav-hotels" },
  { to: "/providers", label: "Proveedores", icon: Building2, tid: "nav-providers" },
];
// Asistente IA section — ADMIN ONLY. Agents do not see these links.
const aiItems = [
  { to: "/ai/generate", label: "Crear desde request", icon: Wand2, tid: "nav-ai-generate" },
  { to: "/ai/trainer", label: "Entrenador del agente", icon: Brain, tid: "nav-ai-trainer" },
];
const adminItems = [
  { to: "/admin/users", label: "Usuarios & Acceso", icon: Users, tid: "nav-admin" },
  { to: "/admin/backups", label: "Copias de seguridad", icon: DatabaseBackup, tid: "nav-backups" },
  { to: "/admin/marketing", label: "Marketing & API", icon: Megaphone, tid: "nav-marketing" },
  { to: "/curated-trips/stats", label: "Estadísticas", icon: BarChart3, tid: "nav-curated-stats" },
];
// Leads KimKim: admins + managers grant (Beatriz, Marina), same as Partners.
const kimkimItem = { to: "/admin/kimkim-leads", label: "Leads KimKim", icon: MessagesSquare, tid: "nav-kimkim-leads" };

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
    isActive
      ? "border-terracotta bg-clay-100 text-clay-900 font-semibold"
      : "border-transparent text-clay-700 hover:bg-clay-100 hover:text-clay-900"
  }`;

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canPartners = isAdmin || !!user?.can_manage_sources;
  const workItems = canPartners
    ? [...navItems, { to: "/partners", label: "Partners", icon: Handshake, tid: "nav-partners" }]
    : navItems;
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-60 shrink-0 border-r border-clay-300 bg-clay-50/60 flex flex-col" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-clay-300 flex items-center gap-2">
          <Compass size={18} className="text-terracotta" />
          <div>
            <div className="font-serif text-lg leading-none">Cartografía</div>
            <div className="smallcaps text-[10px] mt-1">Travel Suite</div>
          </div>
        </div>

        <nav className="flex-1 py-4">
          <div className="smallcaps px-5 mb-2">Trabajo</div>
          {workItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.tid} className={linkClass}>
              <item.icon size={16} />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="smallcaps px-5 mt-6 mb-2">Asistente IA</div>
              {aiItems.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={item.tid} className={linkClass}>
                  <item.icon size={16} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}

          {canPartners && (
            <>
              <div className="smallcaps px-5 mt-6 mb-2">Administración</div>
              {(isAdmin ? [...adminItems, kimkimItem] : [kimkimItem]).map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={item.tid} className={linkClass}>
                  <item.icon size={16} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="border-t border-clay-300 p-4 flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 object-cover rounded-sm border border-clay-300" />
          ) : (
            <div className="w-9 h-9 rounded-sm bg-clay-200 grid place-items-center text-xs font-semibold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[11px] text-clay-700 truncate">{user?.email}</div>
            <div className="smallcaps mt-1" data-testid="user-role">{user?.role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="p-2 hover:bg-clay-200 transition-colors"
            title="Salir"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
